/**
 * Frontend mirror of SedrRequirementTransitions for discharge-status actions.
 * Lifecycle request/approve flows stay in the single-requirement panel.
 * When primary status is ApprovedDischarged, actions operate on renewalStatus.
 */

export type SedrStatusAction = 'markReady' | 'withdraw' | 'deny' | 'approve';

const ACTION_ORDER: SedrStatusAction[] = ['markReady', 'withdraw', 'deny', 'approve'];

/** Outgoing discharge (or renewal) transitions allowed from a status, given approval rights. */
export function allowedStatusActions(
  status: string,
  canApprove: boolean,
  renewalStatus?: string | null,
): SedrStatusAction[] {
  const actions: SedrStatusAction[] = [];

  if (status === 'ApprovedDischarged' && renewalStatus) {
    if (renewalStatus === 'AwaitingMarkReady') {
      actions.push('markReady');
    }
    if (renewalStatus === 'UnderReview') {
      actions.push('withdraw');
      if (canApprove) {
        actions.push('deny', 'approve');
      }
    }
    return actions;
  }

  if (status === 'PendingUpload' || status === 'Denied') {
    actions.push('markReady');
  }
  if (status === 'UnderReview') {
    actions.push('withdraw');
    if (canApprove) {
      actions.push('deny', 'approve');
    }
  }
  return actions;
}

/** Actions present for every status in the selection (intersection). */
export function intersectStatusActions(
  statuses: readonly string[],
  canApprove: boolean,
  renewalStatuses?: readonly (string | null | undefined)[],
): SedrStatusAction[] {
  if (statuses.length === 0) return [];

  let allowed = new Set(
    allowedStatusActions(statuses[0]!, canApprove, renewalStatuses?.[0]),
  );
  for (let i = 1; i < statuses.length; i++) {
    const next = new Set(
      allowedStatusActions(statuses[i]!, canApprove, renewalStatuses?.[i]),
    );
    allowed = new Set([...allowed].filter((action) => next.has(action)));
  }

  return ACTION_ORDER.filter((action) => allowed.has(action));
}

export function statusForAction(action: SedrStatusAction): string {
  switch (action) {
    case 'markReady':
      return 'UnderReview';
    case 'withdraw':
      return 'PendingUpload';
    case 'deny':
      return 'Denied';
    case 'approve':
      return 'ApprovedDischarged';
  }
}
